import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from './ui/icon.component';
import { AnimateDirective } from '../directives/animate.directive';

interface NewsItem {
  category: string;
  date: string;
  title: string;
  summary: string;
  link: string;
  image: string;
}

@Component({
  selector: 'app-news-v2',
  standalone: true,
  imports: [CommonModule, IconComponent, AnimateDirective],
  template: `
    <section id="news" class="min-h-screen flex flex-col justify-center pt-32 pb-32 bg-[#0A0A0A] relative overflow-hidden border-t border-white/5">
      <div class="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FF5500] rounded-full blur-[180px] opacity-[0.03] pointer-events-none"></div>

      <div class="container mx-auto px-4 md:px-6 relative z-10">

        <div class="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div class="max-w-2xl">
            <h2 appAnimate class="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              O MTE está de olho no seu setor. <span class="text-[#FF5500]">Fique à frente da fiscalização.</span>
            </h2>
            <p appAnimate [delay]="100" class="text-gray-300 text-lg">
              As últimas movimentações da legislação, cases reais de autuação e como empresas líderes estão se protegendo.
            </p>
          </div>

          <a href="https://www.gov.br/trabalho-e-emprego/pt-br/acesso-a-informacao/participacao-social/conselhos-e-orgaos-colegiados/comissao-tripartite-partitaria-permanente/normas-regulamentadora/normas-regulamentadoras-vigentes/norma-regulamentadora-no-36-nr-36"
             target="_blank"
             appAnimate [delay]="150"
             class="inline-flex items-center gap-2 text-[#FF5500] hover:text-[#E04800] font-semibold transition-colors group">
            Ver Norma Completa
            <app-icon name="arrow-right" [size]="20" class="transform group-hover:translate-x-1 transition-transform"></app-icon>
          </a>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          @for (item of news; track item.title; let i = $index) {
            <article appAnimate [delay]="200 + (i * 100)"
                     class="bg-[#111111] border border-white/5 rounded-2xl overflow-hidden hover:border-[#FF5500]/30 transition-all duration-300 group hover:-translate-y-2 hover:shadow-2xl hover:shadow-[#FF5500]/10 flex flex-col h-full">

              <div class="relative h-48 w-full overflow-hidden flex-shrink-0">
                <img [src]="item.image" [alt]="item.title" class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                     width="400"
                     height="192"
                     loading="lazy" />
                <div class="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#111111] via-[#111111]/60 to-transparent"></div>
              </div>

              <div class="p-6 flex flex-col h-full relative z-10 -mt-8">
                <div class="flex items-center justify-between mb-4">
                  <span class="px-3 py-1 rounded-full bg-[#FF5500]/10 text-[#FF5500] text-[10px] md:text-xs font-bold uppercase tracking-wider truncate mr-2">
                    {{ item.category }}
                  </span>
                  <span class="text-gray-300 text-xs flex items-center gap-1.5 whitespace-nowrap bg-[#111111]/80 backdrop-blur-sm px-2 py-1 rounded-full">
                    <app-icon name="calendar" [size]="12"></app-icon>
                    {{ item.date }}
                  </span>
                </div>

                <h3 class="text-lg md:text-[1.1rem] leading-tight font-bold text-white mb-3 group-hover:text-[#FF5500] transition-colors line-clamp-3">
                  {{ item.title }}
                </h3>

                <p class="text-gray-300 text-sm leading-relaxed mb-6 flex-grow line-clamp-3">
                  {{ item.summary }}
                </p>

                <a [href]="item.link" target="_blank" class="inline-flex items-center gap-2 text-sm font-medium text-gray-300 group-hover:text-white transition-colors mt-auto pt-5 border-t border-white/5 w-full">
                  Ler matéria completa
                  <app-icon name="external-link" [size]="14"></app-icon>
                </a>
              </div>
            </article>
          }
        </div>

      </div>
    </section>
  `
})
export class NewsV2Component {
  news: NewsItem[] = [
    {
      category: 'Compliance Trabalhista',
      date: '20 de Abril, 2025',
      title: 'TRT-4: Frigorífico indenizará por não conceder pausas a trabalhadora',
      summary: 'Para o colegiado, a empresa não comprovou a concessão de intervalos obrigatórios e, por isso, deverá pagar a empregada valor proporcional ao tempo suprimido, com acréscimo de 50%',
      link: 'https://www.migalhas.com.br/quentes/428334/trt-4-frigorifico-indenizara-por-nao-conceder-pausas-a-trabalhadora',
      image: '/images/noticia-1-opt.webp'
    },
    {
      category: 'Compliance Trabalhista',
      date: '07 de Abril, 2025',
      title: 'Frigorífico deve indenizar trabalhadora que não realizava pausas obrigatórias para segurança e saúde, decide 4ª Turma',
      summary: 'A 4ª Turma do Tribunal Regional do Trabalho da 4ª Região (TRT-RS) condenou um frigorífico ao pagamento de indenização a uma trabalhadora que não realizava pausas psicofisiológicas previstas na Norma Regulamentadora 36 (NR-36).',
      link: 'https://www.trt4.jus.br/portais/trt4/modulos/noticias/50774793',
      image: '/images/noticia-2-opt.webp'
    },
    {
      category: 'Compliance Trabalhista',
      date: '11 de Setembro, 2025',
      title: 'Justiça condena JBS a pagar R$ 200 mil em indenização por subnotificação de acidentes e doenças de trabalho no Pará',
      summary: 'Investigação mostrou que empresa figura entre os maiores índices de subnotificação de acidentes de trabalho e doenças relacionadas ao trabalho no estado.',
      link: 'https://g1.globo.com/pa/para/noticia/2025/09/11/justica-condena-jbs-a-pagar-r-200-mil-em-indenizacao-por-subnotificacao-de-acidentes-e-doencas-de-trabalho-no-para.ghtml',
      image: '/images/noticia-3-opt.webp'
    },
    {
      category: 'Jurisprudência',
      date: '10 de Fevereiro, 2026',
      title: 'TRT-GO reconhece direito ao adicional de insalubridade a trabalhador de frigorífico de Rio Verde por exposição ao frio',
      summary: 'Por unanimidade, a Primeira Turma do Tribunal Regional do Trabalho de Goiás (TRT-GO) julgou improcedente recurso de um frigorífico de Rio Verde e condenou a empresa ao pagamento de adicional de insalubridade em grau médio por expor trabalhador ao frio.',
      link: 'https://www.trt18.jus.br/portal/trt-go-reconhece-direito-ao-adicional-de-insalubridade-a-trabalhador-de-frigorifico-de-rio-verde-por-exposicao-ao-frio/',
      image: '/images/noticia-4-opt.webp'
    }
  ];
}
